import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MpesaIcon } from '@/components/ui/mpesa-icon';
import { cn } from '@/lib/utils';

interface MpesaPaymentSectionProps {
  amount: number;
  orderId: string;
  branchId?: string;
  paymentType?: 'buy_goods' | 'stk_push' | 'both';
  tillNumber?: string;
  supportPhone?: string;
}

export function MpesaPaymentSection({
  amount,
  orderId,
  branchId: _branchId,
  paymentType: _paymentType = 'buy_goods',
  tillNumber: propTillNumber,
  supportPhone: _supportPhone,
}: MpesaPaymentSectionProps) {
  const [copied, setCopied] = useState(false);

  const DEFAULT_TILL_NUMBER = '3022071';
  const tillNumber = propTillNumber || DEFAULT_TILL_NUMBER;
  const verificationHref = `/mpesa?till=${encodeURIComponent(tillNumber)}&amount=${encodeURIComponent(
    amount.toFixed(2),
  )}&orderId=${encodeURIComponent(orderId)}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tillNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="overflow-hidden border-2 border-safaricom-green shadow-lg">
      <CardHeader className="border-b border-safaricom-dark bg-gradient-to-r from-safaricom-green to-safaricom-dark">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white p-2">
            <MpesaIcon className="h-5 w-5" />
          </div>
          <CardTitle className="text-lg font-bold text-white">M-Pesa Till</CardTitle>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 p-6">
        <div className="rounded-2xl border-2 border-safaricom-green bg-gradient-to-br from-gray-950 to-black p-6 text-center text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
            Till Number
          </p>
          <p className="mt-4 font-mono text-5xl font-bold tracking-[0.3em]">
            {tillNumber}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            onClick={copyToClipboard}
            className="bg-safaricom-green text-white hover:bg-safaricom-dark"
            size="lg"
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy till number
              </>
            )}
          </Button>

          <Link
            to={verificationHref}
            className={cn(
              buttonVariants({ variant: 'outline', size: 'lg' }),
              'border-safaricom-green/30 text-safaricom-dark hover:border-safaricom-green hover:bg-mpesa-50',
            )}
          >
            <MpesaIcon className="h-4 w-4" />
            Verify transaction
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
